// Polmoni sopra il gemello del login: albero bronchiale e superficie dei lobi
// come nuvola di punti. Scelta del 17/10/2026 fra otto proposte provate sul
// prototipo («albero + nuvola, l'aria arriva»).
//
// A ogni inspirazione una luce scende dalla trachea lungo i bronchi; dove
// arriva, accende i punti della superficie, dall'ilo verso la periferia. In
// espirazione si ritira nello stesso ordine. Un velo scuro dentro la sagoma dei
// polmoni li stacca dai vasi dell'immagine, altrimenti linee e punti chiari si
// perdono fra i vasi piu' luminosi.
//
// I dati (bg-lungs.json) sono i polmoni veri del caso marketing TTT, proiettati
// in pixel di bg.jpg da scripts/make-login-lungs.py. Ogni punto porta la
// distanza lungo i bronchi a cui lo raggiunge l'aria: e' quella che decide
// quando si accende.
//
// Si disegna in WebGL, nello stesso contesto del gemello. La prima versione
// era un canvas 2D sovrapposto: con un'ombra su ogni ramo in modalita'
// additiva costava 3 s a fotogramma a fine inspirazione, e anche senza ombre
// 14 ms di raster per 600 rami e 7000 punti. Qui la geometria sta sulla GPU
// dal primo fotogramma e il respiro la deforma nel vertex shader con la stessa
// formula dello shader dell'immagine (`displaceGLSL`): per fotogramma restano
// qualche uniform e sei chiamate di disegno.

const FADE = 70 // px di percorso su cui un punto passa da spento ad acceso
const VEIL_ALPHA = 0.5
const TREE_ALPHA = 0.85
const VEIL_RES = 0.5 // risoluzione della maschera del velo, rispetto all'immagine
const VEIL_BLUR = 5 // px immagine
const VEIL_MARGIN = 20
const VEIL_GRID = 16 // il velo e' una griglia, cosi' si deforma col torace
const MITER_LIMIT = 3

// Da JSON compatto a strutture pronte per la geometria.
export function parseLungs(json) {
  const branches = json.airway.map(([d0, d1, r, leaf, ...xy]) => {
    const cum = [0]
    for (let j = 2; j < xy.length; j += 2)
      cum.push(
        cum[cum.length - 1] +
          Math.hypot(xy[j] - xy[j - 2], xy[j + 1] - xy[j - 1])
      )
    return {
      d0,
      d1,
      r,
      leaf: leaf === 1,
      xy,
      cum,
      len: cum[cum.length - 1] || 1,
    }
  })
  return {
    outlines: json.outlines,
    branches,
    points: json.points, // x, y, profondita', distanza di arrivo
    reach: json.reach,
  }
}

// Posizione della luce a un dato volume (0 fine espirazione, 1 fine
// inspirazione): a polmone pieno ha raggiunto anche il punto piu' lontano.
export function lightFront(volume, lungs) {
  return volume * lungs.reach
}

// Rami come strisce di triangoli: per ogni vertice due copie, spostate di
// +-normale (unitaria al centro del ramo, allungata nelle giunzioni). Lo
// spessore lo decide lo shader, strato per strato.
// Vertice: centro x, y, normale x, y, distanza di percorso, d0, d1, raggio.
export const TREE_STRIDE = 8

export function buildTree(lungs) {
  const vertices = []
  const indices = []
  lungs.branches.forEach((b) => {
    const n = b.xy.length / 2
    if (n < 2) return
    const first = vertices.length / TREE_STRIDE
    for (let j = 0; j < n; j += 1) {
      const prev = Math.max(0, j - 1)
      const next = Math.min(n - 1, j + 1)
      let tx = b.xy[2 * next] - b.xy[2 * prev]
      let ty = b.xy[2 * next + 1] - b.xy[2 * prev + 1]
      const tl = Math.hypot(tx, ty) || 1
      tx /= tl
      ty /= tl
      // giunzione: la normale media si allunga per tenere lo spessore
      let scale = 1
      if (j > 0 && j < n - 1) {
        const ax = b.xy[2 * j] - b.xy[2 * j - 2]
        const ay = b.xy[2 * j + 1] - b.xy[2 * j - 1]
        const al = Math.hypot(ax, ay) || 1
        const cos = (ax * tx + ay * ty) / al
        scale = Math.min(MITER_LIMIT, 1 / Math.max(cos, 1 / MITER_LIMIT))
      }
      const d = b.d0 + ((b.d1 - b.d0) * b.cum[j]) / b.len
      ;[1, -1].forEach((side) => {
        vertices.push(
          b.xy[2 * j],
          b.xy[2 * j + 1],
          -ty * scale * side,
          tx * scale * side,
          d,
          b.d0,
          b.d1,
          b.r
        )
      })
      if (j < n - 1) {
        const v = first + 2 * j
        indices.push(v, v + 1, v + 2, v + 1, v + 3, v + 2)
      }
    }
  })
  return {
    vertices: new Float32Array(vertices),
    indices: new Uint16Array(indices),
  }
}

// Punti della superficie: x, y, profondita' (0 davanti), arrivo, indice
// (per lo sfarfallio). Alveoli: estremita' dei rami terminali e la loro d1.
export function buildPoints(lungs) {
  const { points } = lungs
  const cloud = new Float32Array((points.length / 4) * 5)
  for (let i = 0, n = 0; i < points.length; i += 4, n += 1)
    cloud.set(
      [points[i], points[i + 1], points[i + 2], points[i + 3], n],
      n * 5
    )
  const leaves = lungs.branches
    .filter((b) => b.leaf)
    .flatMap((b) => [b.xy[b.xy.length - 2], b.xy[b.xy.length - 1], 0, b.d1, 0])
  return { cloud, alveoli: new Float32Array(leaves) }
}

function outlineBox(lungs) {
  const xs = lungs.outlines.flatMap((o) => o.filter((_, i) => i % 2 === 0))
  const ys = lungs.outlines.flatMap((o) => o.filter((_, i) => i % 2 === 1))
  return [
    Math.min(...xs) - VEIL_MARGIN,
    Math.min(...ys) - VEIL_MARGIN,
    Math.max(...xs) + VEIL_MARGIN,
    Math.max(...ys) + VEIL_MARGIN,
  ]
}

// Griglia del velo sul riquadro dei polmoni: x, y, u, v.
export function buildVeilGrid(box) {
  const [x0, y0, x1, y1] = box
  const vertices = []
  const indices = []
  for (let j = 0; j <= VEIL_GRID; j += 1)
    for (let i = 0; i <= VEIL_GRID; i += 1) {
      const u = i / VEIL_GRID
      const v = j / VEIL_GRID
      vertices.push(x0 + (x1 - x0) * u, y0 + (y1 - y0) * v, u, v)
      if (i < VEIL_GRID && j < VEIL_GRID) {
        const k = j * (VEIL_GRID + 1) + i
        indices.push(k, k + 1, k + VEIL_GRID + 1, k + 1, k + VEIL_GRID + 2)
        indices.push(k + VEIL_GRID + 1)
      }
    }
  return {
    vertices: new Float32Array(vertices),
    indices: new Uint16Array(indices),
  }
}

// Maschera dei polmoni gia' sfocata, disegnata una volta in un canvas 2D e
// caricata come texture. Null se non si puo' creare un canvas (test).
function veilMask(lungs, box, makeCanvas) {
  const canvas = makeCanvas?.()
  const ctx = canvas?.getContext?.('2d')
  if (!ctx) return null
  canvas.width = Math.ceil((box[2] - box[0]) * VEIL_RES)
  canvas.height = Math.ceil((box[3] - box[1]) * VEIL_RES)
  ctx.filter = `blur(${VEIL_BLUR * VEIL_RES}px)`
  ctx.fillStyle = '#fff'
  ctx.setTransform(
    VEIL_RES,
    0,
    0,
    VEIL_RES,
    -box[0] * VEIL_RES,
    -box[1] * VEIL_RES
  )
  lungs.outlines.forEach((outline) => {
    ctx.beginPath()
    ctx.moveTo(outline[0], outline[1])
    for (let j = 2; j < outline.length; j += 2)
      ctx.lineTo(outline[j], outline[j + 1])
    ctx.closePath()
    ctx.fill()
  })
  return canvas
}

// Parte comune dei vertex shader: dal tessuto allo schermo. Lo shader
// dell'immagine mostra in p il tessuto p - displace(p); qui si inverte per
// punto fisso, tre passi bastano (lo spostamento cambia poco fra pixel vicini).
const vertexHeader = (displaceGLSL) => `precision highp float;
uniform vec2 uC, uR, uH, uView;
uniform float uA, uLift, uItto, uS, uOx, uDpr;
${displaceGLSL}
vec2 toScreen(vec2 s) {
  vec2 p = s;
  for (int i = 0; i < 3; i++) p = s + displace(p);
  return p;
}
vec4 toClip(vec2 p) {
  vec2 dev = (p * uS + vec2(uOx, 0.)) * uDpr;
  return vec4(dev.x / uView.x * 2. - 1., 1. - dev.y / uView.y * 2., 0., 1.);
}
`

// Strati: 0 albero di base, 1 aria (tubo largo quanto il lume), 2 aria
// (nucleo piu' chiaro, come il profilo di velocita' in un condotto).
const TREE_VS = (h) => `${h}
attribute vec2 aCenter, aNormal;
attribute vec4 aPath; // distanza, d0, d1, raggio
uniform float uLayer, uFront, uAlpha;
varying float vD;
varying vec4 vColor;
void main() {
  float r = aPath.w;
  float lumen = max(1.4, 1.7 * r);
  float w = uLayer > 1.5 ? max(1., .6 * r) : lumen;
  gl_Position = toClip(toScreen(aCenter + aNormal * .5 * w));
  // tutto lungo il percorso, non per ramo: la distanza e' continua fra un
  // ramo e i figli, d0 e d1 no, e la trachea (due rami) mostrava uno scalino
  float near = max(0., 1. - abs(uFront - aPath.x) / 90.);
  float fade = .5 + .5 * clamp((uFront - aPath.x) / 150., 0., 1.);
  float a = .12 * uAlpha;
  vec3 c = vec3(190., 232., 255.) / 255.;
  vD = -1e6;
  if (uLayer > .5) {
    vD = aPath.x;
    a = uLayer > 1.5 ? (.12 + .28 * near) : (.1 + .12 * near);
    a *= uAlpha * fade;
    c = uLayer > 1.5 ? vec3(215., 242., 255.) / 255. : vec3(170., 225., 255.) / 255.;
  }
  a = min(a, 1.);
  vColor = vec4(c * a, a);
}`

const TREE_FS = `precision highp float;
uniform float uFront;
varying float vD;
varying vec4 vColor;
void main() {
  // ramo illuminato solo fino alla luce, con la testa sfumata: col taglio
  // netto la trachea sembrava fatta a blocchi
  float head = clamp((uFront - vD) / 20., 0., 1.);
  if (head <= 0.) discard;
  gl_FragColor = vColor * head;
}`

// Tipi: 0 punti della superficie, 1 alveoli (tondi, accesi quando la luce
// arriva in fondo al ramo).
const POINT_VS = (h) => `${h}
attribute vec2 aPos;
attribute vec3 aInfo; // profondita', arrivo, indice
uniform float uFront, uT, uAlpha, uKind, uVolume;
varying vec4 vColor;
void main() {
  gl_Position = toClip(toScreen(aPos));
  float a;
  vec3 c;
  if (uKind < .5) {
    float depth = 1. - aInfo.x;
    float lit = clamp((uFront - aInfo.y) / ${FADE.toFixed(1)}, 0., 1.);
    float twinkle = .8 + .2 * sin(uT * 2.3 + aInfo.z);
    a = (.2 + 1.1 * lit * (.4 + .6 * depth)) * twinkle * uAlpha;
    gl_PointSize = (2.4 + 2.4 * depth) * uS * uDpr;
    // azzurro, piu' chiaro davanti: in bianco, sommati, sembravano neve
    c = mix(vec3(55., 140., 240.), vec3(140., 210., 255.), depth) / 255.;
  } else {
    a = uFront >= aInfo.y ? .6 * uAlpha * ${TREE_ALPHA} * uVolume : 0.;
    gl_PointSize = 5. * uS * uDpr;
    c = vec3(110., 195., 255.) / 255.;
  }
  a = min(a, 1.);
  vColor = vec4(c * a, a);
}`

const POINT_FS = `precision highp float;
uniform float uKind;
varying vec4 vColor;
void main() {
  // punti morbidi: il centro pieno, il bordo sfuma
  float r = length(gl_PointCoord - .5);
  gl_FragColor = vColor * smoothstep(.5, uKind > .5 ? .25 : .15, r);
}`

const VEIL_VS = (h) => `${h}
attribute vec2 aPos, aUV;
varying vec2 vUV;
void main() {
  gl_Position = toClip(toScreen(aPos));
  vUV = aUV;
}`

const VEIL_FS = `precision highp float;
uniform sampler2D uMask;
uniform float uOpacity;
varying vec2 vUV;
void main() {
  float m = texture2D(uMask, vUV).a * uOpacity;
  gl_FragColor = vec4(vec3(4., 8., 18.) / 255. * m, m);
}`

const COMMON_UNIFORMS = [
  'uC',
  'uR',
  'uH',
  'uView',
  'uA',
  'uLift',
  'uItto',
].concat(['uS', 'uOx', 'uDpr'])

function program(gl, vs, fs, attributes, uniforms) {
  const p = gl.createProgram()
  ;[
    [gl.VERTEX_SHADER, vs],
    [gl.FRAGMENT_SHADER, fs],
  ].forEach(([type, source]) => {
    const shader = gl.createShader(type)
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    gl.attachShader(p, shader)
  })
  // posizioni fisse: fra un programma e l'altro si abilitano le stesse
  attributes.forEach(([name], i) => gl.bindAttribLocation(p, i, name))
  gl.linkProgram(p)
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) return null
  const loc = {}
  COMMON_UNIFORMS.concat(uniforms).forEach((name) => {
    loc[name] = gl.getUniformLocation(p, name)
  })
  return { p, loc, attributes }
}

function buffer(gl, target, data) {
  const b = gl.createBuffer()
  gl.bindBuffer(target, b)
  gl.bufferData(target, data, gl.STATIC_DRAW)
  return b
}

// Attributi interlacciati: [nome, componenti], nell'ordine del buffer.
function bindAttributes(gl, prog, vbo) {
  const stride = prog.attributes.reduce((sum, [, size]) => sum + size, 0) * 4
  let offset = 0
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo)
  prog.attributes.forEach(([, size], i) => {
    gl.enableVertexAttribArray(i)
    gl.vertexAttribPointer(i, size, gl.FLOAT, false, stride, offset)
    offset += size * 4
  })
}

function unbindAttributes(gl, prog) {
  // la posizione 0 la usa anche il gemello e resta abilitata
  prog.attributes.forEach((_, i) => i && gl.disableVertexAttribArray(i))
}

function defaultCanvas() {
  return typeof document === 'undefined'
    ? null
    : document.createElement('canvas')
}

// Crea lo strato dei polmoni sul contesto del gemello. `shape` e' la forma del
// respiro (centro C, raggi R, cuore H, in pixel immagine), `displaceGLSL` la
// funzione displace(p) dello shader dell'immagine. Null se i programmi non si
// collegano: in quel caso il gemello respira senza polmoni.
export function createLungLayer(
  gl,
  lungs,
  { shape, displaceGLSL, makeCanvas = defaultCanvas }
) {
  const h = vertexHeader(displaceGLSL)
  const tree = program(
    gl,
    TREE_VS(h),
    TREE_FS,
    [
      ['aCenter', 2],
      ['aNormal', 2],
      ['aPath', 4],
    ],
    ['uLayer', 'uFront', 'uAlpha']
  )
  const points = program(
    gl,
    POINT_VS(h),
    POINT_FS,
    [
      ['aPos', 2],
      ['aInfo', 3],
    ],
    ['uFront', 'uT', 'uAlpha', 'uKind', 'uVolume']
  )
  const veil = program(
    gl,
    VEIL_VS(h),
    VEIL_FS,
    [
      ['aPos', 2],
      ['aUV', 2],
    ],
    ['uMask', 'uOpacity']
  )
  if (!tree || !points || !veil) return null

  const treeGeo = buildTree(lungs)
  const { cloud, alveoli } = buildPoints(lungs)
  const box = outlineBox(lungs)
  const grid = buildVeilGrid(box)
  const buffers = {
    tree: buffer(gl, gl.ARRAY_BUFFER, treeGeo.vertices),
    treeIdx: buffer(gl, gl.ELEMENT_ARRAY_BUFFER, treeGeo.indices),
    cloud: buffer(gl, gl.ARRAY_BUFFER, cloud),
    alveoli: buffer(gl, gl.ARRAY_BUFFER, alveoli),
    veil: buffer(gl, gl.ARRAY_BUFFER, grid.vertices),
    veilIdx: buffer(gl, gl.ELEMENT_ARRAY_BUFFER, grid.indices),
  }
  const mask = veilMask(lungs, box, makeCanvas)
  let maskTexture = null
  if (mask) {
    maskTexture = gl.createTexture()
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, maskTexture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mask)
  }

  const common = (prog, u, view) => {
    const { loc } = prog
    gl.useProgram(prog.p)
    gl.uniform2f(loc.uC, shape.C[0], shape.C[1])
    gl.uniform2f(loc.uR, shape.R[0], shape.R[1])
    gl.uniform2f(loc.uH, shape.H[0], shape.H[1])
    gl.uniform2f(loc.uView, view.width, view.height)
    gl.uniform1f(loc.uA, u.breathAmp)
    gl.uniform1f(loc.uLift, u.lift)
    gl.uniform1f(loc.uItto, u.itto)
    gl.uniform1f(loc.uS, view.scale)
    gl.uniform1f(loc.uOx, view.offsetX)
    gl.uniform1f(loc.uDpr, view.dpr)
  }

  // Un fotogramma. `u` sono le uniform del gemello (frameUniforms), `view` la
  // scala cover, lo scarto e la misura del canvas in pixel del dispositivo.
  const draw = ({ t, u, intensity, view }) => {
    const front = lightFront(u.volume, lungs)
    gl.enable(gl.BLEND)

    if (maskTexture) {
      common(veil, u, view)
      gl.activeTexture(gl.TEXTURE2)
      gl.bindTexture(gl.TEXTURE_2D, maskTexture)
      gl.uniform1i(veil.loc.uMask, 2)
      gl.uniform1f(veil.loc.uOpacity, Math.min(0.8, VEIL_ALPHA * intensity))
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      bindAttributes(gl, veil, buffers.veil)
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.veilIdx)
      gl.drawElements(gl.TRIANGLES, grid.indices.length, gl.UNSIGNED_SHORT, 0)
      unbindAttributes(gl, veil)
    }

    gl.blendFunc(gl.ONE, gl.ONE) // luce che si somma, come 'lighter'
    common(points, u, view)
    gl.uniform1f(points.loc.uFront, front)
    gl.uniform1f(points.loc.uT, t)
    gl.uniform1f(points.loc.uAlpha, intensity)
    gl.uniform1f(points.loc.uVolume, u.volume)
    gl.uniform1f(points.loc.uKind, 0)
    bindAttributes(gl, points, buffers.cloud)
    gl.drawArrays(gl.POINTS, 0, cloud.length / 5)

    common(tree, u, view)
    gl.uniform1f(tree.loc.uFront, front)
    gl.uniform1f(tree.loc.uAlpha, intensity * TREE_ALPHA)
    bindAttributes(gl, tree, buffers.tree)
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.treeIdx)
    ;[0, 1, 2].forEach((layer) => {
      gl.uniform1f(tree.loc.uLayer, layer)
      gl.drawElements(
        gl.TRIANGLES,
        treeGeo.indices.length,
        gl.UNSIGNED_SHORT,
        0
      )
    })
    unbindAttributes(gl, tree)

    common(points, u, view)
    gl.uniform1f(points.loc.uKind, 1)
    bindAttributes(gl, points, buffers.alveoli)
    gl.drawArrays(gl.POINTS, 0, alveoli.length / 5)
    unbindAttributes(gl, points)

    gl.disable(gl.BLEND)
  }

  return { draw }
}
